import { View, CollectionView, EventListener, KeyCodes } from "silver";

const html = `
	<div class="listbox" data-myon-on-scroll="handleScroll">
		<div class="listbox_message g_ellipsis" data-myon-if="{{ showMessage }}">
			{{ message @noescape }}
		</div>
		<ul class="listbox_options" tabindex="0"
			data-myon-on-keyup="handleKeyUp"
			data-myon-on-keydown="handleKeyDown"
			role="listbox"
			>
			<li role="option" data-myon-attr-class="listbox_option {{ isFocused? | '-focus' }} {{ isSelected? | '-selected' }}" id="{{ option.id }}"
				data-myon-attr-aria-selected="{{ isSelected | boolString }}"
				data-myon-template="options"
				data-myon-click="handleItemClick"
				data-myon-on-mouseover="handleItemMouseover"
				data-myon-on-mouseout="handleItemMouseout">
				{{ option.node @noescape }}
			</li>
		</ul>
	</div>`;


export type ListboxOption<ID extends number|string = number|string> = {
	id: ID,
	node: Node
};

type ListboxOptionViewData<O extends ListboxOption> = {
	option: O,
	isFocused: boolean;
	isSelected: boolean;
};

type ListboxViewData<O extends ListboxOption> = {
	options: Record<string, ListboxOptionViewData<O>>;
	message: Node;
	showMessage: boolean;
}

type ListboxView<O extends ListboxOption> = View<ListboxViewData<O>> & {
	options: CollectionView<ListboxOptionViewData<O>>;
}

function randomString(): string {
	return Math.random().toString(36).substring(7);
}

export class Listbox<ID extends number|string, Option extends ListboxOption<ID> = ListboxOption<ID>> {
	readonly onModified = EventListener.create<{ sender: Listbox<ID, Option>}>("modified");
	readonly onScroll = EventListener.create<{ sender: Listbox<ID, Option> }>("scroll");
	readonly onSelect = EventListener.create<{ sender: Listbox<ID, Option>, value: ID }>("select");
	private view: ListboxView<Option>;
	private focused?: ID;
	public readonly id: string;

	constructor() {
		this.id = "listbox-" + randomString();
		this.view = new View<ListboxViewData<Option>>(html, {
			boolString:  (v: boolean) => v ? "true" : "false",
			handleKeyUp: (event: KeyboardEvent) => {
				if (!this.focused) {
					return;
				}
				if (event.keyCode === KeyCodes.SPACE || event.keyCode === KeyCodes.ENTER) {
					this.select(this.focused);
					return false;
				}
			},
			handleScroll: () => {
				this.onScroll.trigger({ sender: this });
			},
			handleKeyDown: (event: KeyboardEvent) => {
				if (!this.focused) {
					return;
				}
				const name = String(this.focused);
				if (!this.view.options.has(name)) {
					return;
				}
				const el = this.view.options.get(name).element;
				let next: null|Element = null;
				if (event.keyCode === KeyCodes.UP_ARROW) {
					next = el.previousElementSibling;
				} else if (event.keyCode === KeyCodes.DOWN_ARROW) {
					next = el.nextElementSibling;
				}
				if (next) {
					const nextView = this.view.options.get(next.id);
					this.focus(nextView.dataset.option.id);
					this.scrollIntoView(nextView.dataset.option.id);
					event.preventDefault();
				}
			},
			handleItemClick: (e: Event, data: ListboxOptionViewData<Option>): false|void => {
				this.select(data.option.id);
			},
			handleItemMouseover: (e: Event, data: ListboxOptionViewData<Option>): false|void => {
				this.focus(data.option.id);
			},
			handleItemMouseout: (e: Event, data: ListboxOptionViewData<Option>, view: View<ListboxOptionViewData<Option>>): false|void => {
				view.set({ isFocused: false });
			}
		}, {
			message:     document.createTextNode(""),
			showMessage: false,
			options:     { }
		}) as ListboxView<Option>;
	}

	public set selectable(value: boolean) {
		this.view.element.classList.toggle("-selectable", value);
	}

	public get selectable(): boolean {
		return this.view.element.classList.contains("-selectable");
	}

	public showMessage(message: string|Node): void {
		if (typeof message === "string") {
			message = document.createTextNode(message);
		}

		this.view.dataset.showMessage = true;
		this.view.dataset.message = message;
	}

	public hideMessage(): void {
		this.view.dataset.showMessage = false;
	}

	public scrollIntoView(item: ID): void {
		const view = this.getItemView(item);
		if (!view) {
			return;
		}
		const viewTop = view.element.offsetTop;
		const viewBottom = view.element.offsetHeight + viewTop;
		const scrollTop = this.view.element.scrollTop;
		const scrollBottom = scrollTop + this.view.element.offsetHeight;
		if (viewTop >= scrollTop && viewBottom <= scrollBottom) {
			return;
		}
		view.element.scrollIntoView(false);
	}

	private getItemView(item: ID): View<ListboxOptionViewData<Option>>|undefined {
		let out: View<ListboxOptionViewData<Option>>|undefined;
		this.view.options.forEach((view) => {
			if (view.dataset.option.id === item) {
				out = view;
			}
		});
		return out;
	}

	public set height(height: number|null) {
		this.view.element.classList.toggle("-overflow", height !== null);
		this.view.element.style.height = (height === null ? "" : `${height}px`);
	}

	public get height(): number|null {
		if (!this.view.element.classList.contains("-overflow")) {
			return null;
		}
		return parseInt(this.view.element.style.height, 10);
	}

	/**
	 * @param {number} roundToPageSize When provided, this will return items rounded to the nearest value, so
	 * that you may retrieve items paged. This is useful for batch operations
	 */
	public getVisibleOptions(roundToPageSize: number = 1): Option[] {
		const root = this.element;
		const scrollTop = root.scrollTop;
		const scrollBottom = scrollTop + root.offsetHeight;
		const items: Array<{ option: Option, visible: boolean }> = [];
		this.view.options.forEach((view) => {
			const itemTop = view.element.offsetTop;
			const itemBottom = itemTop + view.element.offsetHeight;
			const visible = ((itemBottom >= scrollTop && itemBottom <= scrollBottom) || (itemTop >= scrollTop && itemTop <= scrollBottom));
			items.push({ option: view.dataset.option, visible });
		});
		let start: number = Infinity;
		let end: number = -Infinity;
		for (let i = 0; i < items.length; i++) {
			if (items[i].visible) {
				const pageStart = Math.floor(i / roundToPageSize) * roundToPageSize;
				start = Math.min(start, pageStart);
				end = Math.max(end, pageStart + roundToPageSize);
			}
		}
		return items.slice(start, end).map(i => i.option);
	}

	get element(): HTMLElement {
		return this.view.element;
	}

	select(id: ID, clearSelection: boolean = true): void {
		if (!this.selectable) {
			return;
		}
		this.view.options.forEach((view) => {
			if (!clearSelection && view.dataset.option.id !== id) {
				return;
			}
			view.set({ isSelected: view.dataset.option.id === id });
		});
		this.onSelect.trigger({ value: id, sender: this });
	}

	focus(id?: ID): void {
		this.view.options.forEach((view, viewValue) => {
			const isFocused = viewValue == id;
			view.set({ isFocused });
			if (isFocused) {
				this.focused = id;
			}
		});
	}

	add(option: Option): void {
		this.addMany([ option ]);
	}

	addMany(entries: Option[]): void {
		entries.forEach(option => {
			if (this.view.options.has(String(option.id))) {
				console.warn(`View alreaady has item with id "${option.id}"`);
				return;
			}
			this.view.options.add(option.id, {
				option,
				isFocused:  false,
				isSelected: false,
			});
			if (!this.focused) {
				this.focus(option.id);
			}
		});
		this.onModified.trigger({ sender: this });
	}

	sort(sorter: (a: ID, b: ID) => number): void {
		this.view.options.sort((aa:  View<ListboxOptionViewData<Option>>, bb: View<ListboxOptionViewData<Option>>): number => {
			return sorter(aa.dataset.option.id, bb.dataset.option.id);
		});
	}

	clear(): void {
		this.view.options.empty();
		this.onModified.trigger({ sender: this });
	}

	contains(id: ID): boolean {
		return this.view.options.has(String(id));
	}

	isEmpty(): boolean {
		return this.view.options.count() === 0;
	}

	getOption(id: ID): Option|undefined {
		if (!this.view.options.has(String(id))) {
			return undefined;
		}
		return this.view.options.get(String(id)).dataset.option;
	}

	getAllOptions(): Option[] {
		const out: Option[] = [];
		this.view.options.forEach((view) => {
			out.push(view.dataset.option);
		});
		return out;
	}

	getSelectedOptions(): Option[] {
		const out: Option[] = [];
		this.view.options.forEach((view) => {
			if (view.get("isSelected")) {
				out.push(view.dataset.option);
			}
		});
		return out;
	}

	remove(id: ID): void {
		this.view.options.remove(String(id));
		this.onModified.trigger({ sender: this });
	}
}
